/**
 * Phase N — /methodology
 *
 * Public methodology page. Renders LIVE verification counts from
 * /api/catalog/verification-summary. No fabricated numeric claims.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import LegalReviewBanner from "@/components/LegalReviewBanner";
import { api } from "@/lib/api";

function Eyebrow({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">{children}</div>;
}

function Section({ eyebrow, title, id, testId, children }) {
  return (
    <section id={id} data-testid={testId} className="border-b border-stone/20 py-14 sm:py-16">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-ivy tracking-tight">{title}</h2>
      <div className="mt-6 max-w-3xl text-ink-soft leading-relaxed space-y-4">{children}</div>
    </section>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div className="border border-stone/40 bg-bone-dim p-6 text-center" data-testid={testId}>
      <div className="font-serif text-4xl sm:text-5xl text-ivy tabular-nums" data-testid={`${testId}-value`}>
        {value === null || value === undefined ? "—" : value}
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">{label}</div>
    </div>
  );
}

export default function Methodology() {
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/catalog/verification-summary")
      .then((r) => { if (!cancelled) setSummary(r.data); })
      .catch((e) => { if (!cancelled) setErr(String(e)); });
    return () => { cancelled = true; };
  }, []);

  return (
    <Layout>
      <SEO
        title="Methodology | Beyond Valencia"
        description="How Beyond Valencia verifies every claim. Verified requirement graphs, source URLs on every prerequisite, live verification counts, and disciplined AI use."
        canonical="/methodology"
        ogType="article"
      />
      <LegalReviewBanner />

      {/* Hero */}
      <section className="bg-ivy text-bone" data-testid="methodology-hero">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 pb-16">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft font-semibold">Methodology</div>
          <h1
            className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            data-testid="methodology-h1"
          >
            Every claim on this platform maps to a source URL.
          </h1>
          <p className="mt-6 max-w-2xl text-bone-dim text-lg leading-relaxed">
            Beyond Valencia is built on a verified requirement graph. We publish counts, not adjectives. This page
            explains how the graph is built, how AI is used, and what standard every claim on the site has to meet.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        {/* Live counts */}
        <section className="py-12 border-b border-stone/20" data-testid="methodology-live-counts">
          <Eyebrow>Live verification counts</Eyebrow>
          <h2 className="mt-3 font-serif text-2xl sm:text-3xl text-ivy tracking-tight">
            The state of the graph, updated in real time.
          </h2>
          <p className="mt-4 text-sm text-stone">
            Data source:{" "}
            <code className="text-xs bg-bone-dim border border-stone/30 px-1.5 py-0.5">
              GET /api/catalog/verification-summary
            </code>
            {summary && summary.generated_at && (
              <>
                {" · Generated "}
                <span data-testid="methodology-generated-at">
                  {new Date(summary.generated_at).toLocaleString()}
                </span>
              </>
            )}
          </p>
          {err && (
            <p className="mt-3 text-sm text-crimson" data-testid="methodology-error">
              Unable to load current counts. The values shown below may be cached.
            </p>
          )}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard
              label="Institutions verified"
              value={summary?.institutions_verified}
              testId="stat-institutions-verified"
            />
            <StatCard
              label="Majors verified"
              value={summary?.majors_verified}
              testId="stat-majors-verified"
            />
            <StatCard
              label="Roadmaps verified"
              value={summary?.roadmaps_verified}
              testId="stat-roadmaps-verified"
            />
            <StatCard
              label="Programs verified"
              value={summary?.programs_verified}
              testId="stat-programs-verified"
            />
            <StatCard
              label="Verification records"
              value={summary?.verification_records_verified}
              testId="stat-verification-records-verified"
            />
            <StatCard
              label="Prereqs traceable"
              value={summary?.program_requirements_traceable}
              testId="stat-program-requirements-traceable"
            />
          </div>
          <p className="mt-4 text-xs text-stone leading-relaxed">
            These figures are aggregated live from our verified requirement graph and do not include placeholder,
            draft, or awaiting-review records.
          </p>
        </section>

        <Section
          eyebrow="Core principle"
          title="No claim ships without a source."
          id="core-principle"
          testId="methodology-core-principle"
        >
          <p>
            Every prerequisite, GPA threshold, credit floor, articulation rule, and destination-program requirement
            on the platform is backed by at least one verification record. A verification record contains a source
            URL (public catalog page, Common Prerequisites Manual entry, articulation agreement, or tracking sheet),
            a source title, a note, and a last-verified date.
          </p>
          <p>
            Unverified data is not exposed on the public site. It sits in a draft state in the admin catalog until
            an editor attaches a defensible source and re-runs verification.
          </p>
        </Section>

        <Section
          eyebrow="How the graph is built"
          title="Institution → program → requirement → source."
          id="graph"
          testId="methodology-graph"
        >
          <p>
            The graph starts with institutions (community colleges and destination universities), branches into
            programs (majors and specializations at each destination), and terminates in requirements
            (prerequisites, GPA floors, credit thresholds). Every edge is attached to at least one source URL.
          </p>
          <p>
            When a student runs the Mishap Corrector diagnostic or a paid audit, the underlying evaluation walks
            this graph — matching Valencia (or any supported community-college) coursework to the destination
            program&apos;s requirement set, using the same sources that appear on the public pair pages.
          </p>
        </Section>

        <Section
          eyebrow="AI discipline"
          title="Language models rewrite; they do not invent."
          id="ai"
          testId="methodology-ai"
        >
          <p>
            AI is used in two narrow places: rewriting deterministic templates on programmatic pair pages, and
            drafting per-student summary language in the paid diagnostic. In both cases the AI is constrained by
            an injection-schema prompt, its output is validated server-side against an allow-list of course codes
            and banned adjectives, and any hallucinated code triggers a fallback to the raw template.
          </p>
          <p>
            AI does not decide admissions probabilities, invent GPA thresholds, or select target schools. Humans do.
          </p>
        </Section>

        <Section
          eyebrow="Editorial review"
          title="Publish, then re-verify on a schedule."
          id="editorial"
          testId="methodology-editorial"
        >
          <p>
            Verified records carry a last-verified date. Records older than the site&apos;s review interval are
            surfaced in the admin verification queue and re-checked against the underlying source. When the source
            has changed, the record is either updated with a new note and last-verified date, or moved back to
            draft.
          </p>
          <p>
            Every AI-generated pair-page intro is regenerable and cached with its model version so we can invalidate
            entire batches when catalog rules change.
          </p>
        </Section>

        <Section
          eyebrow="What we do not do"
          title="No fabricated statistics. No inflated outcomes." id="do-not"
          testId="methodology-do-not"
        >
          <p>
            We do not publish acceptance rates that we did not measure. We do not invent testimonials. We do not
            round unverified figures. We do not describe our footprint using numbers that we cannot defend from
            audited records.
          </p>
          <p>
            The live counts above are the numbers you should hold us accountable to. If a page on this site cites a
            figure and you cannot find it on{" "}
            <Link to="/methodology" className="text-ivy hover:text-brass underline">this page</Link>{" "}
            or the underlying source URL, tell us — see the{" "}
            <Link to="/about#contact" className="text-ivy hover:text-brass underline" data-testid="methodology-about-link">
              About page
            </Link>{" "}
            for contact routes.
          </p>
        </Section>
      </div>
    </Layout>
  );
}
